import { setInteractionModality } from '@react-aria/interactions';

const reveal = (el: HTMLElement) => {
  // The user reached here by clicking "View Problem" or a submit button, so
  // React Aria is in pointer modality and would suppress the focus ring on
  // the field we jump to. Flip to keyboard modality so isFocusVisible turns
  // on and the regular focusRing styles render; the next real pointer event
  // resets it.
  setInteractionModality('keyboard');
  el.focus({ preventScroll: true });
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
};

const queryField = (name: string): HTMLElement | undefined => {
  // Step 1: Native inputs expose their name directly on the focusable element.
  // Exclude hidden inputs and React Aria's off-screen <select> (tabindex="-1")
  // which exists solely for form autofill and is positioned at top:0/left:0.
  const native = document.querySelector<HTMLElement>(
    `[name="${name}"]:not([type="hidden"]):not([tabindex="-1"])`
  );
  if (native) return native;

  // Step 2: Composite widgets carry data-rhf-name on a wrapper or the trigger.
  // Use getAttribute for exact string comparison — avoids CSS selector parsing
  // of dots/brackets that appear in nested field names like "section.field".
  const wrapper = Array.from(
    document.querySelectorAll<HTMLElement>('[data-rhf-name]')
  ).find((el) => el.getAttribute('data-rhf-name') === name);
  if (wrapper) {
    return (
      wrapper.querySelector<HTMLElement>(
        'button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex="0"]'
      ) ?? wrapper
    );
  }

  // Step 3: React Aria Select fallback — navigate from the hidden <select>
  // (div > label > select[tabIndex=-1]) to the visible trigger button.
  const hiddenSelect = Array.from(
    document.querySelectorAll<HTMLSelectElement>('select[tabindex="-1"]')
  ).find((el) => el.name === name);
  const hiddenContainer = hiddenSelect?.parentElement?.parentElement;
  const triggerDiv =
    hiddenContainer?.previousElementSibling as HTMLElement | null;
  return (
    triggerDiv?.querySelector<HTMLElement>('button:not([disabled])') ??
    undefined
  );
};

// Inputs are registered bracket-style; also try the dotted equivalent.
const findField = (path: string): HTMLElement | undefined => {
  const dotPath = path.replace(/\[(\d+)]/g, '.$1');
  const paths = path === dotPath ? [path] : [path, dotPath];
  return paths
    .map(queryField)
    .find((el): el is HTMLElement => el instanceof HTMLElement);
};

export const focusFirstProblem = (paths: string[]): boolean => {
  const target =
    paths.reduce<HTMLElement | undefined>(
      (found, path) => found ?? findField(path),
      undefined
    ) ??
    // Group-level errors (e.g. "add at least one material") have no named
    // input; fall back to whatever is flagged invalid or alerting.
    document.querySelector('[aria-invalid="true"], [role="alert"]');

  if (target instanceof HTMLElement) {
    reveal(target);
    return true;
  }

  return false;
};
